import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Play, 
  RefreshCw,
  Languages,
  MapPin,
  Target,
  TrendingUp
} from 'lucide-react';
import { CityTranslationEnforcer } from '@/utils/validation/CityTranslationEnforcer';
import { worldCities } from '@/data/cities';
import { CityLanguage, cityLanguageNames, cityLanguageFlags } from '@/utils/cityLanguages';

interface EnforcementState {
  isRunning: boolean;
  progress: number;
  currentCity: string;
  currentLanguage: string;
  results: any;
  error: string | null;
}

const GlobalTranslationEnforcementPage = () => {
  const [enforcementState, setEnforcementState] = useState<EnforcementState>({
    isRunning: false,
    progress: 0,
    currentCity: '',
    currentLanguage: '',
    results: null,
    error: null
  });

  const handleStartEnforcement = async () => {
    setEnforcementState(prev => ({
      ...prev,
      isRunning: true,
      progress: 0,
      error: null,
      results: null
    }));

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setEnforcementState(prev => {
          if (prev.progress < 90) {
            return {
              ...prev,
              progress: prev.progress + Math.random() * 10,
              currentCity: worldCities[Math.floor(Math.random() * worldCities.length)].name,
              currentLanguage: Object.keys(cityLanguageNames)[Math.floor(Math.random() * Object.keys(cityLanguageNames).length)]
            };
          }
          return prev;
        });
      }, 500);

      const results = await CityTranslationEnforcer.enforceGlobalTranslations();
      
      clearInterval(progressInterval);
      
      setEnforcementState(prev => ({
        ...prev,
        isRunning: false,
        progress: 100,
        results,
        currentCity: '',
        currentLanguage: ''
      }));
    } catch (error) {
      setEnforcementState(prev => ({
        ...prev,
        isRunning: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
        currentCity: '',
        currentLanguage: ''
      }));
    }
  };

  const handleQuickValidation = async (citySlug: string, language: CityLanguage) => {
    try {
      const result = await CityTranslationEnforcer.quickValidateCity(citySlug, language);
      alert(`${citySlug} (${language}): ${result.coverage.toFixed(1)}% coverage, ${result.hardcodedStrings} hardcoded strings`);
    } catch (error) {
      alert('Validation failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const getCoverageColor = (coverage: number) => {
    if (coverage >= 95) return 'text-green-600';
    if (coverage >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCoverageBadgeVariant = (coverage: number) => {
    if (coverage >= 95) return 'default';
    if (coverage >= 80) return 'secondary';
    return 'destructive';
  };

  return (
    <>
      <Helmet>
        <title>Global Translation Enforcement System</title>
        <meta name="description" content="Enforce 100% translation coverage across all cities and languages" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
              <Globe className="h-8 w-8" />
              Global Translation Enforcement System
            </h1>
            <p className="text-muted-foreground">
              Ensure 100% translation coverage for all {worldCities.length} cities across {Object.keys(cityLanguageNames).length} languages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Total Cities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{worldCities.length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Languages className="h-4 w-4" />
                  Languages
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{Object.keys(cityLanguageNames).length}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Target Coverage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">100%</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Current Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {enforcementState.results 
                    ? `${enforcementState.results.globalCoverage.toFixed(1)}%`
                    : '---'
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="enforcement" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="enforcement">Global Enforcement</TabsTrigger>
              <TabsTrigger value="validation">Quick Validation</TabsTrigger>
              <TabsTrigger value="results">Results Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="enforcement" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Global Translation Enforcement
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Run comprehensive translation enforcement across all cities and languages
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex gap-4">
                    <Button 
                      onClick={handleStartEnforcement}
                      disabled={enforcementState.isRunning}
                      className="flex items-center gap-2"
                    >
                      {enforcementState.isRunning ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {enforcementState.isRunning ? 'Running...' : 'Start Global Enforcement'}
                    </Button>
                  </div>

                  {enforcementState.isRunning && (
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Progress</span>
                          <span className="text-sm text-muted-foreground">
                            {enforcementState.progress.toFixed(0)}%
                          </span>
                        </div>
                        <Progress value={enforcementState.progress} className="w-full" />
                      </div>

                      {enforcementState.currentCity && (
                        <div className="text-sm text-muted-foreground">
                          Processing: <span className="font-medium">{enforcementState.currentCity}</span> 
                          {enforcementState.currentLanguage && (
                            <> in <span className="font-medium">{cityLanguageNames[enforcementState.currentLanguage as CityLanguage]}</span></>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {enforcementState.error && (
                    <div className="flex items-center gap-2 p-4 bg-destructive/10 rounded-lg">
                      <XCircle className="h-5 w-5 text-destructive" />
                      <span className="text-destructive">{enforcementState.error}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="validation" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Validation</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Validate translation coverage for specific cities and languages
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {worldCities.slice(0, 12).map(city => (
                      <Card key={city.slug} className="p-4">
                        <h4 className="font-medium mb-2">{city.name}</h4>
                        <div className="flex flex-wrap gap-1">
                          {(['en', 'es', 'fr', 'de'] as CityLanguage[]).map(lang => (
                            <Button
                              key={lang}
                              variant="outline"
                              size="sm"
                              className="text-xs p-1 h-6"
                              onClick={() => handleQuickValidation(city.slug, lang)}
                            >
                              {cityLanguageFlags[lang]} {lang.toUpperCase()}
                            </Button>
                          ))}
                        </div>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="results" className="mt-6">
              {enforcementState.results ? (
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Enforcement Results
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">
                            {enforcementState.results.globalCoverage.toFixed(1)}%
                          </div>
                          <div className="text-sm text-muted-foreground">Global Coverage</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            {enforcementState.results.totalIssuesFixed.toLocaleString()}
                          </div>
                          <div className="text-sm text-muted-foreground">Issues Fixed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-purple-600">
                            {(enforcementState.results.executionTime / 1000).toFixed(1)}s
                          </div>
                          <div className="text-sm text-muted-foreground">Execution Time</div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {enforcementState.results.results.slice(0, 10).map((cityResult: any, index: number) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded">
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{cityResult.cityName}</span>
                              <Badge variant={getCoverageBadgeVariant(cityResult.overallCoverage)}>
                                {cityResult.overallCoverage.toFixed(1)}%
                              </Badge>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {cityResult.totalIssuesFixed} issues fixed
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card>
                  <CardContent className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No results available. Run the global enforcement first.</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default GlobalTranslationEnforcementPage;